import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Input, message, Popconfirm, Spin, Table, Typography } from 'antd';
import Icon from '@ant-design/icons';
import UnlockSvg from '@icons/unlock.svg';

const { Text } = Typography;

import { useCreatePermissions } from '@hooks/usePermissions';
import {
  useGetBlockVideoEvents,
  usePostUnblockVideoEvents,
  usePostVideoBlockEvents,
} from '@api/emp/queryHooks';
import { generateSelectOptions } from '@src/emp-3.0/utils/buildOptions';
import { getFilteredSortedData } from '@src/emp-3.0/utils/management';
import {
  BLOCK_VIDEO_EVENTS,
  dateFields,
  handleAction,
  supportSearchFields,
} from '@src/emp-3.0/constants/management';
import blockSvg from '@icons/event-management/unblock.svg';

import {
  ManagementTable,
  SearchFilterPanel,
  HeaderRenderWrapper,
  BlockVideoEventInputWrapper,
  useBlockVideoEventsManagementStore,
  generateColumn,
} from '..';
import moment from 'moment';
import TableWithSearch from '../components/TableWithSearch';

const BlockVideoEvents = () => {
  const hasCreate = useCreatePermissions();

  const {
    unblockSelectedList,
    setUnblockSelectedList,

    // Filter related state
    showFilter,
    searchText,
    setSearchText,
    setShowFilter,

    // Filter conditions
    blockedBy,
    blockedDate,
    setBlockedBy,
    setBlockedDate,

    // Sort state
    sort,
    setSort,

    // Reset
    resetFilter,
    resetStore,
  } = useBlockVideoEventsManagementStore((state) => state);

  const [blockEventName, setBlockEventName] = useState('');

  const {
    data: blockVideoEventsList,
    isFetching: isLoadingBlockVideoEventsList,
    refetch: refetchBlockVideoEventsList,
  } = useGetBlockVideoEvents();

  const { mutate: unblockEvents, isLoading: isLoadingUnblockEvents } = usePostUnblockVideoEvents();
  const { mutate: blockEvents, isLoading: isLoadingBlockEvents } = usePostVideoBlockEvents();

  const isLoading = useMemo(
    () => isLoadingBlockVideoEventsList || isLoadingUnblockEvents || isLoadingBlockEvents,
    [isLoadingBlockEvents, isLoadingBlockVideoEventsList, isLoadingUnblockEvents],
  );

  useEffect(() => {
    refetchBlockVideoEventsList();
  }, []);

  const allBlockVideoEventsNames = useMemo(
    () => (blockVideoEventsList?.data ?? []).map((item) => item.event_name),
    [blockVideoEventsList?.data],
  );

  useEffect(() => {
    const newList = unblockSelectedList.filter((name) => allBlockVideoEventsNames.includes(name));
    setUnblockSelectedList(newList);
  }, [allBlockVideoEventsNames]);

  const filteredSortedData = getFilteredSortedData({
    dataList: blockVideoEventsList?.data,
    filterParams: {
      blockedDate,
      blockedBy,
    },
    searchParams: {
      searchText,
      supportSearchFields,
      dateFields,
    },
    sortParams: sort,
  });
  const tableData = useMemo(
    () =>
      filteredSortedData.map((item) => ({
        id: item.event_id,
        ...item,
      })),
    [filteredSortedData],
  );

  const handleUnblockEvent = useCallback(
    ({ rowData }: { rowData: any }) => {
      if (rowData) {
        handleAction({
          action: unblockEvents,
          params: { events: [rowData.event_name] },
          onSuccess: refetchBlockVideoEventsList,
          successMessage: `${rowData.event_name} has been unblocked from sensor.`,
          errorMessage: 'Failed to unblock, please try again later!',
          showSensorDelayMessage: true,
        });
      }
    },
    [refetchBlockVideoEventsList, unblockEvents],
  );
  const unblockConfirmTitle = (
    <div
      style={{
        width: '404px',
        fontWeight: 500,
        letterSpacing: '-0.14px',
        lineHeight: '20px',
        color: '#333',
      }}
    >
      Unblock this item? Once unblocked, it will be removed from the Blocked Events and sensor data
      reporting will resume.
    </div>
  );

  const handleBlockEvent = useCallback(() => {
    if (!blockEventName) {
      return message.warning('Please add a video event');
    }
    // Do the block, and clear the block event name after success.
    return handleAction({
      action: blockEvents,
      params: { events: [{ event_name: blockEventName }] },
      onSuccess: () => {
        setBlockEventName('');
        refetchBlockVideoEventsList();
      },
      successMessage: `${blockEventName} has been blocked from sensor.`,
      showSensorDelayMessage: true,
    });
  }, [blockEventName, blockEvents, refetchBlockVideoEventsList]);

  const handleSearch = useCallback(
    (value) => {
      setSearchText(value);
    },
    [setSearchText],
  );

  const handleShowFilterChange = useCallback(
    (value: boolean) => {
      if (!value) {
        resetFilter();
      }
      setShowFilter(value);
    },
    [resetFilter, setShowFilter],
  );

  const columns: ColumnsType<any> = [
    {
      title: 'Blocked Video Event',
      dataIndex: 'event_name',
      render: (text, record) => {
        return (
          <span id="videoEventName" data-test-id={text}>
            {text}
          </span>
        );
      },
    },

    {
      title: 'Blocked By',
      dataIndex: 'block_by',
      render: (text, record) => {
        return (
          <span id="videoEventBlockedBy" data-test-id={text}>
            {text}
          </span>
        );
      },
    },
    {
      title: 'Blocked Date',
      dataIndex: 'block_timestamp',
      render: (ts, record) => {
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span id="blockedDate" data-test-id={moment.unix(ts).format('YYYY/MM/DD HH:mm')}>
              {moment.unix(ts).format('YYYY/MM/DD HH:mm')}
            </span>

            {hasCreate ? (
              <Popconfirm
                title={unblockConfirmTitle}
                okText="Yes"
                cancelText="No"
                placement="topRight"
                onConfirm={() => handleUnblockEvent({ rowData: record })}
              >
                <Button
                  type="default"
                  id="unblockVideoEventsButton"
                  data-test-id={`unblockButton-${record.event_name}`}
                  icon={<UnlockSvg style={{ marginRight: 8 }} />}
                  style={{
                    color: '#1677FF',
                    borderColor: '#1677FF',
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontWeight: 500,
                  }}
                  className="custom-unblock-button"
                >
                  Unblock
                </Button>
              </Popconfirm>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <Spin spinning={isLoading}>
      <div style={{ padding: '8px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span
            style={{ fontSize: '20px', fontWeight: '500' }}
            data-test-id="blockedVideoEventsHeader"
          >
            Blocked Video Events
          </span>
        </div>

        <div style={{ width: '95%', marginTop: 8 }}>
          <Text
            style={{ fontSize: '14px', color: '#8c8c8c', fontWeight: 400 ,width: '95%'}}
            data-test-id="blockedVideoEventsDescription"
          >
            This feature allows you to efficiently restrict the collection of streaming events.
            Limiting data ingestion to the most relevant and meaningful events from the sensor
            ensures accuracy and relevancy of your experience-centric data.
          </Text>
        </div>
        <div>
          {hasCreate ? (
            <BlockVideoEventInputWrapper>
              <Input
                id="blockVideoEventInput"
                value={blockEventName}
                onChange={(e) => setBlockEventName(e.target.value)}
                placeholder="Please input the video event name"
              />
              <Button
                type="primary"
                onClick={() => {
                  // Check for duplicate event name before blocking
                  const exists = tableData.some(
                    (item) =>
                      (item.event_name || '').trim().toLowerCase() ===
                      (blockEventName || '').trim().toLowerCase()
                  );
                  if (exists) {
                    message.error('This video event name is already blocked.');
                    return;
                  }
                  handleBlockEvent();
                }}
                data-test-id="blockVideoEventButton"
              >
                <Icon component={blockSvg} />
                Block Event
              </Button>
            </BlockVideoEventInputWrapper>
          ) : null}

          <TableWithSearch
            data={tableData}
            columns={columns}
            pagination={false}
            rowKey={(record) => record.event_id || `${record.parent?.event_id}-detail`}
            searchKeys={['event_name', 'event_type', 'block_by']} // Based on your data structure
          />
        </div>
      </div>
    </Spin>
  );
};

export default BlockVideoEvents;
